import { useMemo, type JSX } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { JizdaListItem } from '@/api/types/Jizda';

import { CalendarJizdaRow } from './CalendarJizdaRow';
import { groupDayByCommodity } from './grouping';

export interface CalendarDayColumnProps {
  items: JizdaListItem[];
  onOpen: (id: string) => void;
}

// The body of one day (CHR-03 §6 / ADR-0051): its jízdy grouped by commodity
// (contiguous, with a commodity sub-header), each commodity bucket ordered by
// loading place → unloading place → plannedArrival. Shared between the desktop
// 7-column grid and the mobile day-accordion (the column header / chrome is
// the caller's responsibility, so this renders only the grouped body).
export const CalendarDayColumn = ({
  items,
  onOpen,
}: CalendarDayColumnProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');

  const groups = useMemo(() => groupDayByCommodity(items), [items]);

  if (groups.length === 0) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          color: theme.palette.text.disabled,
          textAlign: 'center',
          py: theme.spacing(1),
        }}
        data-testid="calendar-day-empty"
      >
        {t('calendar.emptyDay')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
      }}
    >
      {groups.map((group) => (
        <Box
          key={group.key}
          data-testid={`calendar-commodity-group-${group.key}`}
          sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.75) }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: theme.palette.text.secondary,
            }}
          >
            {group.commodityName ?? t('calendar.noCommodity')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.75) }}>
            {group.items.map((item) => (
              <CalendarJizdaRow key={item.id} item={item} onOpen={onOpen} />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
